/**
 * Quiz Script API 클라이언트 (quiz/quiz_script 테이블 기반)
 */
import { apiClient } from './client'
import type {
  QuizScript,
  QuizSolveRequest,
  SolveResult,
} from '@quebon/shared-learning'

/**
 * vars를 Base64 인코딩하여 헤더로 전달
 */
function encodeVarsToBase64(vars: Record<string, unknown>): string {
  return btoa(JSON.stringify(vars))
}

/**
 * Quiz Script API 클라이언트 구현
 * Note: quizId는 JS Number.MAX_SAFE_INTEGER를 초과할 수 있으므로 string 사용
 */
export const quizScriptApi = {
  /**
   * 퀴즈 조회 (quiz_id로)
   */
  getQuiz: async (
    quizId: string,
    showAnswer = false,
    vars?: Record<string, unknown>,
    useFixedVars?: boolean
  ): Promise<QuizScript> => {
    const headers: Record<string, string> = {}
    if (vars) {
      headers['x-qb-quiz-vars-base64'] = encodeVarsToBase64(vars)
    }

    const response = await apiClient.get(`/quiz/quizzes/${quizId}`, {
      params: { showAnswer, useFixedVars },
      headers,
    })
    return response.data.data
  },

  /**
   * 퀴즈 목록 조회
   */
  getQuizzes: async (
    ids: string[],
    showAnswer = false,
    useFixedVars?: boolean
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get('/quiz/quizzes', {
      params: { ids: ids.join(','), showAnswer, useFixedVars },
    })
    return response.data.data
  },

  /**
   * 퀴즈 풀이
   */
  solve: async (
    quizId: string,
    request: QuizSolveRequest
  ): Promise<SolveResult> => {
    const response = await apiClient.post(
      `/quiz/quizzes/${quizId}/solve`,
      request
    )
    return response.data.data
  },

  /**
   * Wrapup 퀴즈 조회 (quiz.adult=true, quiz_location=post)
   * 
   * QuizReviewPage 등에서 성인용 마무리 문제를 조회할 때 사용
   */
  getWrapupQuizzes: async (
    contentId: number,
    showAnswer = false,
    questionCount?: number,
    includeAiq = false
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get(
      `/quiz/contents/${contentId}/quizzes/wrapup`,
      {
        params: { showAnswer, questionCount, includeAiq },
      }
    )
    return response.data.data
  },

  /**
   * Product 단위 AIQ 퀴즈 조회
   * 해당 Product 내 모든 레슨의 AIQ 퀴즈를 배치로 가져옴
   */
  getProductAiqQuizzes: async (
    productId: number,
    questionCount?: number,
    shuffle = false,
    showAnswer = false
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get(
      `/quiz/product/${productId}/aiq`,
      {
        params: { questionCount, shuffle, showAnswer },
      }
    )
    return response.data.data
  },

  /**
   * 타이틀로 퀴즈 검색
   * title에 특정 문자열이 포함된 퀴즈를 검색
   */
  searchQuizzesByTitle: async (
    titleContains: string,
    showAnswer = false
  ): Promise<QuizScript[]> => {
    const response = await apiClient.get('/quiz/quizzes/search', {
      params: { titleContains, showAnswer },
    })
    return response.data.data
  },
}

export default quizScriptApi
